import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import NamedLink from '../NamedLink/NamedLink';

import css from './CartExpiredDatesNotice.module.css';

export const CART_EXPIRED_NOTICE_VARIANT_SIDE_CART = 'sideCart';
export const CART_EXPIRED_NOTICE_VARIANT_CART_PAGE = 'cartPage';

const BODY_MESSAGE_ID = {
  [CART_EXPIRED_NOTICE_VARIANT_SIDE_CART]: 'CartExpiredDatesNotice.bodySideCart',
  [CART_EXPIRED_NOTICE_VARIANT_CART_PAGE]: 'CartExpiredDatesNotice.bodyCartPage',
};

/**
 * "These dates have expired." notice for a cart whose start day has passed
 * (checkExpiredRentalDates, util/cartHelpers.js). One component for every cart
 * surface - both side carts above their date editor, and the cart page under an
 * expired item - so the treatment can't drift between them.
 *
 * Deliberately quiet (client feedback 2026-09-18): a warning-coloured left border on
 * a light panel, muted icon and body, only the lead sentence in red. The dates being
 * expired is a fact to act on, not a failure to alarm about. `variant` picks the body
 * copy for the host; the lead and the button read the same everywhere.
 *
 * The button hands off to the host: `onUpdateDates` opens the cart's own date editor
 * in place. A host with no editor to open (the cart page for an item whose listing is
 * gone from the cart's data) passes `updateDatesLinkProps` instead and the button is a
 * NamedLink to the listing page's fix-expired flow. role="alert" so screen readers
 * announce it on open.
 */
const CartExpiredDatesNotice = ({ className, variant, onUpdateDates, updateDatesLinkProps }) => (
  <div role="alert" className={classNames(css.root, className)}>
    <p className={css.message}>
      <i className="ri-time-line" aria-hidden="true" />
      <span className={css.text}>
        <strong className={css.lead}>
          <FormattedMessage id="CartExpiredDatesNotice.lead" />
        </strong>{' '}
        <FormattedMessage id={BODY_MESSAGE_ID[variant] || BODY_MESSAGE_ID[CART_EXPIRED_NOTICE_VARIANT_SIDE_CART]} />
      </span>
    </p>
    {updateDatesLinkProps && !onUpdateDates ? (
      <NamedLink {...updateDatesLinkProps} className={css.button}>
        <FormattedMessage id="CartExpiredDatesNotice.updateDates" />
      </NamedLink>
    ) : (
      <button type="button" className={css.button} onClick={onUpdateDates}>
        <FormattedMessage id="CartExpiredDatesNotice.updateDates" />
      </button>
    )}
  </div>
);

CartExpiredDatesNotice.defaultProps = {
  className: null,
  variant: CART_EXPIRED_NOTICE_VARIANT_SIDE_CART,
  onUpdateDates: null,
  updateDatesLinkProps: null,
};

CartExpiredDatesNotice.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    CART_EXPIRED_NOTICE_VARIANT_SIDE_CART,
    CART_EXPIRED_NOTICE_VARIANT_CART_PAGE,
  ]),
  onUpdateDates: PropTypes.func,
  // NamedLink props ({ name, params, to }) - used only when there is no onUpdateDates.
  updateDatesLinkProps: PropTypes.object,
};

export default CartExpiredDatesNotice;
